import * as THREE from 'three';
import { performScan, removeViewLineById } from './viewAnalyser';
import { useViewScoreStore } from '@/store/viewScoreStore';

export interface ViewAnalysisProgress {
	current: number;
	total: number;
	windowName: string;
}

// Helper to highlight current window being analyzed
const highlightWindow = (scene: THREE.Scene, windowTarget: THREE.Object3D | null) => {
	// Remove previous highlight
	const prevHighlight = scene.getObjectByName('__currentWindowHighlight__');
	if (prevHighlight) {
		scene.remove(prevHighlight);
		if (prevHighlight instanceof THREE.Mesh) {
			prevHighlight.geometry.dispose();
			if (prevHighlight.material instanceof THREE.Material) {
				prevHighlight.material.dispose();
			}
		}
	}

	// Add new highlight
	if (windowTarget) {
		const highlightGeometry = new THREE.SphereGeometry(0.4, 16, 16);
		const highlightMaterial = new THREE.MeshBasicMaterial({
			color: 0x00ff00,
			transparent: true,
			opacity: 0.5,
			depthTest: false,
		});
		const highlightMesh = new THREE.Mesh(highlightGeometry, highlightMaterial);
		highlightMesh.position.copy(windowTarget.position);
		highlightMesh.name = '__currentWindowHighlight__';
		highlightMesh.renderOrder = 9999;
		scene.add(highlightMesh);
	}
};

export const runViewAnalysisForAllWindows = async (
	camera: THREE.PerspectiveCamera,
	scene: THREE.Scene,
	selectedBuildingId?: string,
	onProgress?: (progress: ViewAnalysisProgress) => void
): Promise<void> => {
	// Find all window view point targets in the scene
	const windowTargets: THREE.Object3D[] = [];
	scene.traverse((obj) => {
		if (!obj.userData.isViewPointTarget) {
			return;
		}

		if (selectedBuildingId && obj.userData.buildingId !== selectedBuildingId) {
			return;
		}

		if (obj.userData.isViewPointTarget) {
			windowTargets.push(obj);
		}
	});

	if (windowTargets.length === 0) {
		if (selectedBuildingId) {
			console.warn('No window view points found for selected building');
		} else {
			console.warn('No window view points found in scene');
		}
		return;
	}

	// Run view analysis for each window sequentially
	for (let i = 0; i < windowTargets.length; i++) {
		const target = windowTargets[i];
		
		// Highlight current window
		highlightWindow(scene, target);
		
		if (onProgress) {
			onProgress({
				current: i,
				total: windowTargets.length,
				windowName: target.name || `Window ${i + 1}`,
			});
		}
		
		try {
			await setCameraView(camera, scene, target);
		} catch (error) {
			console.error(`Error analyzing window ${target.name}:`, error);
		}
	}

	// Remove highlight when done
	highlightWindow(scene, null);

	// Final progress update
	if (onProgress) {
		onProgress({
			current: windowTargets.length,
			total: windowTargets.length,
			windowName: 'Complete',
		});
	}
};

export const setCameraView = async (
	camera: THREE.PerspectiveCamera,
	scene: THREE.Scene,
	object: THREE.Object3D
) => {
	let outsideDir = object.userData.outsideDirection;
	if (!(outsideDir instanceof THREE.Vector3) && outsideDir) {
		outsideDir = new THREE.Vector3(outsideDir.x, outsideDir.y, outsideDir.z);
	}
	if (outsideDir && outsideDir.lengthSq() > 0) {
		outsideDir = outsideDir.clone().normalize();

		const spherePos = object.position.clone();

		const buildingMesh = scene.getObjectByProperty('uuid', object.userData.buildingId);
		const buildingCenter = new THREE.Vector3(0, 0, 0);
		if (buildingMesh instanceof THREE.Mesh && buildingMesh.geometry) {
			const bbox = new THREE.Box3().setFromObject(buildingMesh);
			bbox.getCenter(buildingCenter);
		}

		// Ensure outside direction points away from building center
		const fromCenter = spherePos.clone().sub(buildingCenter).normalize();
		if (outsideDir.dot(fromCenter) < 0) {
			outsideDir.negate();
		}

		const cameraOffset = 0.1;
		const cameraPos = spherePos.clone().add(outsideDir.clone().multiplyScalar(cameraOffset));

		const targets = scene.children.filter(
			(child) =>
				child.type !== 'DirectionalLight' &&
				child.type !== 'AmbientLight' &&
				child.type !== 'CameraHelper' &&
				child.type !== 'LineSegments' &&
				!child.userData.isViewRaySamplingPlane &&
				!child.userData.ignoreSceneClick &&
				!child.userData.isRoomVolume &&
				!child.userData.isShadowIndicator &&
				!child.userData.isLuxTooltip &&
				!child.userData.isViewPointTarget
		);

		// Perform view analysis scan with explicit direction
		// const scan = await performScanSync({
		// 	scene,
		// 	camera,
		// 	originPoint: cameraPos,
		// 	viewDirection: outsideDir, // Add explicit view direction
		// 	targets,
		// 	// categoryFilter: new Set(['vegetation', 'water']),
		// });
		
		// Use stable window identifier for storing view lines/planes.
		// Position fallback is rounded to avoid tiny floating-point drift creating duplicates.
		const parentWindowUuid = object.parent?.userData?.windowUuid;
		const stablePosKey = `${spherePos.x.toFixed(4)},${spherePos.y.toFixed(4)},${spherePos.z.toFixed(4)}`;
		const windowId = object.userData.windowUuid || parentWindowUuid || object.uuid || stablePosKey;
		
		await performScan({
			scene,
			camera,
			originPoint: cameraPos,
			viewDirection: outsideDir, // Add explicit view direction
			targets,
			setDebugGeo: () => {},
			setAnalysisData: (data) => {
				const existingData = useViewScoreStore.getState().viewData;
				const pointKey = `${spherePos.x},${spherePos.y},${spherePos.z}`;
				const updatedData = {
					...existingData,
					[pointKey]: data,
				};
				useViewScoreStore.getState().setViewData(updatedData);

				const { horizonContexts, isBeyondHorizonEnabled, setViewSettingsForPoint } =
					useViewScoreStore.getState();
				setViewSettingsForPoint(pointKey, {
					horizonContexts: [...horizonContexts],
					isBeyondHorizonEnabled,
				});
			},
			onProgress: (e) => {
				useViewScoreStore.getState().setViewScoreProgress(e);
			},
			windowId, // Pass window identifier to keep lines for each window
			// categoryFilter: new Set(['open']),
		});
	}
};

export const runViewAnalysisFromFacadePoint = async (
	camera: THREE.PerspectiveCamera,
	scene: THREE.Scene,
	facadePoint: THREE.Vector3,
	facadeNormal: { x: number; y: number; z: number }
) => {
	// Convert facade normal to THREE.Vector3 and normalize
	const outsideDir = new THREE.Vector3(
		facadeNormal.x,
		facadeNormal.y,
		facadeNormal.z
	).normalize();

	// Position camera slightly offset from facade point
	const cameraOffset = 0.1;
	const cameraPos = facadePoint.clone().add(outsideDir.clone().multiplyScalar(cameraOffset));

	// Get all scene objects excluding UI/helper elements
	const targets = scene.children.filter(
		(child) =>
			child.type !== 'DirectionalLight' &&
			child.type !== 'AmbientLight' &&
			child.type !== 'CameraHelper' &&
			child.type !== 'LineSegments' &&
			!child.userData.isViewRaySamplingPlane &&
			!child.userData.ignoreSceneClick &&
			!child.userData.isRoomVolume &&
			!child.userData.isShadowIndicator &&
			!child.userData.isLuxTooltip &&
			!child.userData.isViewPointTarget
	);

	// Use facade point position as unique identifier
	const facadeId = `facade_${facadePoint.x.toFixed(2)}_${facadePoint.y.toFixed(2)}_${facadePoint.z.toFixed(2)}`;

	// Perform view analysis from facade point
	await performScan({
		scene,
		camera,
		originPoint: cameraPos,
		viewDirection: outsideDir,
		targets,
		setDebugGeo: () => {},
		setAnalysisData: (data) => {
			const existingData = useViewScoreStore.getState().viewData;
			const updatedData = {
				...existingData,
				[facadeId]: data,
			};
			useViewScoreStore.getState().setViewData(updatedData);

			const { horizonContexts, isBeyondHorizonEnabled, setViewSettingsForPoint } =
				useViewScoreStore.getState();
			setViewSettingsForPoint(facadeId, {
				horizonContexts: [...horizonContexts],
				isBeyondHorizonEnabled,
			});
		},
		onProgress: (e) => {
			useViewScoreStore.getState().setViewScoreProgress(e);
		},
		windowId: facadeId,
	});
};

/**
 * Find and remove view analysis near a given point (within threshold distance)
 * Returns true if an analysis was removed, false otherwise
 */
export const removeNearbyViewAnalysis = (
	point: THREE.Vector3,
	scene: THREE.Scene,
	thresholdMeters: number = 1.0
): boolean => {
	const viewData = useViewScoreStore.getState().viewData;
	
	for (const key of Object.keys(viewData)) {
		let existingPoint: THREE.Vector3 | null = null;
		
		// Handle facade format: "facade_x_y_z"
		if (key.startsWith('facade_')) {
			const parts = key.replace('facade_', '').split('_').map(parseFloat);
			if (parts.length === 3) {
				existingPoint = new THREE.Vector3(parts[0], parts[1], parts[2]);
			}
		} else {
			// Handle window format: "x,y,z"
			const coords = key.split(',').map(parseFloat);
			if (coords.length === 3) {
				existingPoint = new THREE.Vector3(coords[0], coords[1], coords[2]);
			}
		}
		
		if (existingPoint) {
			const distance = point.distanceTo(existingPoint);
			
			if (distance <= thresholdMeters) {
				const updatedData = { ...viewData };
				delete updatedData[key];
				useViewScoreStore.getState().setViewData(updatedData);

				const settingsByPoint = { ...useViewScoreStore.getState().viewSettingsByPoint };
				delete settingsByPoint[key];
				useViewScoreStore.getState().setViewSettingsByPoint(settingsByPoint);
				
				removeViewLineById(key, scene);
				
				return true;
			}
		}
	}
	
	return false;
};


